import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountCircle,
  EventBusy,
  LinkOff,
  // Aliased: the bare name `Lock` resolves to the Web Locks API interface in
  // lib.dom, which is not a component and fails as JSX.
  Lock as LockIcon,
} from "@mui/icons-material";
import React from "react";
import { NavigateFunction } from "react-router-dom";

import { Buffer } from "buffer";

import { environment } from "../utils/environment";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import {
  CodeController,
  CodeControllerDelegate,
  ICodeDestination,
  normalizeCode,
} from "./controller";

/**
 * `form` is the only state that invites another attempt. The other three are
 * terminal on purpose: a locked code, a closed window and a registered account
 * are all situations where retyping cannot help, and offering the field again
 * would just teach the person to keep trying.
 */
type Phase = "form" | "locked" | "closed" | "usePassword";

interface State {
  code: string;
  isLoading: boolean;
  phase: Phase;
  error?: string;
  message?: string;
}

/** Exactly the backend's ACCESS_CODE_LENGTH — the field never holds more. */
const CODE_LENGTH = 6;

/** One size for every terminal illustration, so the four screens sit alike. */
const TERMINAL_ICON_SX = { fontSize: 72 };

/** The palette each terminal state speaks in — see the call sites for why. */
type TerminalTone = "error" | "warning" | "info" | "action";

/**
 * The icon's `color` token mapped to the palette path Typography understands.
 * One tone drives both, so a title can never end up a different colour from the
 * icon above it.
 */
const TERMINAL_TITLE_COLOR: Record<TerminalTone, string> = {
  error: "error.main",
  warning: "warning.main",
  info: "info.main",
  action: "action.active",
};

interface Props {
  navigationHooks: NavigateFunction;
}

class CodeScreen
  extends React.Component<Props, State>
  implements CodeControllerDelegate {
  private controller: CodeController;
  private invitationUid: string | null;
  private sessionExpired: boolean;

  constructor(props: Props) {
    super(props);
    this.controller = new CodeController(this);

    const params = new URLSearchParams(window.location.search);
    // `invitation`, never `code`: on this screen "código" is the 6-char
    // credential the person types, so the uid must not share that name.
    this.invitationUid = params.get("invitation");
    // Set by logout() when a code session dies, so the person is told why they
    // are back here instead of guessing.
    this.sessionExpired = params.get("expired") === "1";

    this.state = {
      code: "",
      isLoading: false,
      phase: "form",
    };
  }

  // DELEGATE

  /**
   * Navigates through the router, never `window.location.href`: Instructions and
   * Questions read `instrumentId` / `applicationId` out of react-router state in
   * their constructors, and a full page load arrives with `state === null`,
   * which crashes the screen before it renders.
   *
   * The three destinations mirror what an assignment card resolves to on the
   * list, because a code session lands here holding whatever state its coleta is
   * already in — including finished, since codes stay valid until the due date
   * so the person can still reach their devolutiva.
   */
  didAuthenticate(destination: ICodeDestination): void {
    const navigate = this.props.navigationHooks;

    if (destination.isFinished) {
      navigate("/instrument/end", {
        state: {
          data: {
            assignmentId: destination.assignmentId,
            applicationId: destination.applicationId,
            instrumentId: destination.instrumentId,
            instrumentTitle: destination.instrumentTitle,
            firstTimeFinished: false,
          },
        },
      });
      return;
    }

    // Past the due date there is nothing to open; the list shows such a card
    // without a link, so send the person to that list rather than into a screen
    // whose token request would only answer 410.
    if (destination.isLate) {
      navigate("/");
      return;
    }

    navigate(`/instructions/${destination.assignmentId}`, {
      state: {
        data: {
          instrumentId: destination.instrumentId,
          applicationId: destination.applicationId,
        },
      },
    });
  }

  codeRejected(message: string): void {
    this.setState({ isLoading: false, error: message });
  }

  lockedOut(message: string): void {
    this.setState({ isLoading: false, phase: "locked", message });
  }

  windowClosed(message: string): void {
    this.setState({ isLoading: false, phase: "closed", message });
  }

  shouldUsePassword(message: string): void {
    this.setState({ isLoading: false, phase: "usePassword", message });
  }

  serviceUnavailable(message: string): void {
    this.setState({ isLoading: false, error: message });
  }

  // INTERNALS

  /**
   * Normalizes as the person types and caps at the code's real length, so the
   * field holds the canonical 6 characters and nothing else.
   *
   * The cap lives here rather than in the input's `maxLength` on purpose: the
   * browser would clip a pasted "abc-234" to "abc-23" before this ever ran,
   * losing the last character. Normalizing first absorbs the separators, then
   * the slice bites.
   */
  private handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = normalizeCode(event.target.value).slice(0, CODE_LENGTH);
    this.setState({ code, error: undefined });
  };

  private handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!this.invitationUid) return;

    this.setState({ isLoading: true, error: undefined });
    await this.controller.login(this.invitationUid, this.state.code);
  };

  /**
   * A dead end, so there is nothing to interact with — which leaves an empty
   * screen unless something carries the weight. `icon` is that something: it
   * gives the state a shape the person reads before the words.
   */
  private renderTerminal(
    title: string,
    body: string,
    Icon: React.ElementType,
    tone: TerminalTone,
    action?: React.ReactNode
  ) {
    return (
      <Box sx={{ width: "100%", maxWidth: 420, px: 2, textAlign: "center" }}>
        <Box sx={{ mb: 2 }}>
          <Icon sx={TERMINAL_ICON_SX} color={tone} />
        </Box>
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          fontWeight={600}
          color={TERMINAL_TITLE_COLOR[tone]}
        >
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {body}
        </Typography>
        {/* Only the states with a real next step get one; the others would be
            offering a button that cannot change anything. */}
        {action && <Box sx={{ mt: 3 }}>{action}</Box>}
      </Box>
    );
  }

  /**
   * Sends the person to the auth-fe login, the same handshake LoginGuard uses:
   * clientId + redirectUrl back to /auth, and `state` naming where to land
   * afterwards.
   *
   * The state deliberately points at the assignments list rather than
   * `window.location.href` — returning someone who just authenticated with a
   * password to the code screen would be a loop back to the wrong door.
   */
  private goToLogin = () => {
    const authData = environment.auth;
    const clientId = encodeURIComponent(authData.clientId);
    const redirectUrl = encodeURIComponent(authData.redirectUrl);
    const state = encodeURIComponent(
      Buffer.from(JSON.stringify({ url: environment.baseUrl })).toString(
        "base64"
      )
    );
    window.location.href = decodeURIComponent(
      `${authData.baseUrl}/?clientId=${clientId}&redirectUrl=${redirectUrl}&state=${state}`
    );
  };

  private renderBody() {
    const { isLoading, error, code, phase, message } = this.state;

    // A link without the uid cannot identify anyone; sending the person to the
    // login would be a dead end, so say what is wrong instead.
    //
    // Neutral grey, not amber: nothing is wrong with the coleta — the link
    // itself arrived broken, which is a different kind of problem from the
    // three below and should not borrow their colours.
    if (!this.invitationUid) {
      return this.renderTerminal(
        "Link incompleto",
        "Abra o link exatamente como ele chegou no seu e-mail. Se o problema continuar, peça um novo e-mail ao seu psicólogo.",
        LinkOff,
        "action"
      );
    }

    // Red: the one state that needs someone to act before the person can get in.
    if (phase === "locked") {
      return this.renderTerminal(
        "Acesso bloqueado",
        "Muitas tentativas. Peça um novo código a quem encaminhou o instrumento para você responder.",
        LockIcon,
        "error"
      );
    }

    // Amber, matching the admin listing's `expired` chip — same fact, two views.
    if (phase === "closed") {
      return this.renderTerminal(
        "Prazo encerrado",
        // Appended, not a fallback: the backend always sends a message here
        // (due date passed, or the application was archived), so anything in the
        // `||` branch would never render. The guidance has to ride along with
        // whichever of the two arrived — this is the one terminal state that
        // named no way forward.
        `${message || "O prazo para responder este instrumento encerrou."
        } Se ainda precisar responder, fale com o profissional que enviou o convite.`,
        EventBusy,
        "warning"
      );
    }

    // Blue: not a failure at all, just a different door — so it must not look
    // like the two states above.
    if (phase === "usePassword") {
      return this.renderTerminal(
        "Você já tem uma conta",
        message ||
        "Este convite já foi convertido em conta. Entre com seu e-mail e senha.",
        AccountCircle,
        "info",
        <Button
          variant="contained"
          fullWidth
          onClick={this.goToLogin}
          sx={{ color: "white", height: 48 }}
        >
          Entrar com e-mail e senha
        </Button>
      );
    }

    return (
      <Box sx={{  maxWidth: 420, px: 2 }}>
        <Typography variant="h4" gutterBottom textAlign="center" color="primary" fontWeight={600}>
          Entrar com código
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3 }}
          textAlign="center"
        >
          Digite o código que você recebeu por e-mail. Você não precisa criar
          uma conta.
        </Typography>

        {this.sessionExpired && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Sua sessão expirou. Digite o código novamente para continuar — suas
            respostas foram salvas.
          </Alert>
        )}

        <Box component="form" onSubmit={this.handleSubmit}>
          <TextField
            label="Código de acesso"
            variant="outlined"
            fullWidth
            value={code}
            onChange={this.handleCodeChange}
            error={!!error}
            helperText={error}
            disabled={isLoading}
            autoFocus
            inputProps={{
              // No `maxLength`: handleCodeChange normalizes then caps, and a DOM
              // limit would clip a pasted separator-bearing code first.
              inputMode: "text",
              autoCapitalize: "characters",
              autoCorrect: "off",
              spellCheck: false,
              style: {
                textTransform: "uppercase",
                letterSpacing: "0.3em",
                fontFamily: "monospace",
                fontSize: "1.4rem",
                textAlign: "center",
              },
            }}
          />
          <Box sx={{ position: "relative", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              // Only a full-length code may be submitted: a short one is certain
              // to fail, and every failure counts toward the 5-attempt lockout
              // that would strand the respondent for 15 minutes.
              disabled={isLoading || code.length < CODE_LENGTH}
              sx={{ color: "white", height: 48 }}
            >
              Acessar instrumento
            </Button>
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  render() {
    return (
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        alignItems="center"
        justifyContent="center"
        minHeight="100dvh"
        py={0}
        sx={{ backgroundColor: "grey.100" }}
      >
        {/* No Header, and no LoginGuard either: this screen IS the login for a
            code session, so the person arrives with no token — an app bar would
            only offer a home link that bounces them straight back here. The
            bare logo carries the branding without implying a way out.

            It sits inside the centred column rather than pinned to the top, so
            logo and content are balanced together as one block. */}
        <Box
          component="img"
          src="/images/logo.png"
          alt="Catvante"
          sx={{ maxHeight: { xs: 60, md: 72 }, mb: 4 }}
        />
        {this.renderBody()}
      </Box>
    );
  }
}

export default withNavigationHooks(CodeScreen);
